import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { type TaxIdFieldConfig } from '../../../shared/model';
import {
  PersonalDataInputField,
  rubikTypographyStyles,
} from '../../../shared/ui';
import {
  type PersonalDataFieldValues,
  type PersonalDataFieldId,
} from '../model';

type PersonalDataFormSectionProps = {
  taxIdErrorMessage: string | null;
  isTaxIdInvalid: boolean;
  onOpenTaxIdHelp: () => void;
  onFieldChange: (fieldId: PersonalDataFieldId, nextValue: string) => void;
  taxIdField: TaxIdFieldConfig;
  values: PersonalDataFieldValues;
};

const PersonalDataFormContainer = styled.section.attrs({
  'data-ui': 'registration-identity-personal-data-form',
})`
  width: ${({ theme }) => theme.scale.px(353)};
  align-self: center;
  display: grid;
  gap: ${({ theme }) => theme.scale.px(5)};
`;

const PersonalDataFormTitle = styled.h1`
  ${rubikTypographyStyles};
  width: ${({ theme }) => theme.scale.px(341)};
  margin: 0;
  white-space: pre-line;
  font-size: ${({ theme }) => theme.scale.px(24)};
  font-weight: 600;
  line-height: ${({ theme }) => theme.scale.px(28)};
`;

const IdentityFieldsTrack = styled.div`
  display: grid;
  gap: ${({ theme }) => theme.scale.px(20)};
`;

export function PersonalDataFormSection({
  taxIdErrorMessage,
  isTaxIdInvalid,
  onOpenTaxIdHelp,
  onFieldChange,
  taxIdField,
  values,
}: PersonalDataFormSectionProps) {
  const { t } = useTranslation();
  const fieldDescriptors = [
    {
      fieldId: 'firstName' as const,
      label: t('registrationIdentityPersonalData.firstNameLabel'),
      placeholder: t('registrationIdentityPersonalData.firstNamePlaceholder'),
    },
    {
      fieldId: 'lastName' as const,
      label: t('registrationIdentityPersonalData.lastNameLabel'),
      placeholder: t('registrationIdentityPersonalData.lastNamePlaceholder'),
    },
    {
      fieldId: 'taxId' as const,
      inputMode: 'numeric' as const,
      label: t(taxIdField.labelKey),
      mask: taxIdField.mask,
      placeholder: taxIdField.placeholder,
      showHelpTip: taxIdField.showHelpTip,
      tipAriaLabel: taxIdField.tipAriaLabelKey
        ? t(taxIdField.tipAriaLabelKey)
        : undefined,
    },
  ];

  const handleFieldChange = (fieldId: PersonalDataFieldId) => (value: string) =>
    onFieldChange(fieldId, value);

  return (
    <PersonalDataFormContainer>
      <PersonalDataFormTitle>
        {t('registrationIdentityPersonalData.title')}
      </PersonalDataFormTitle>

      <IdentityFieldsTrack>
        {fieldDescriptors.map(fieldDescriptor => {
          const isTaxIdField = fieldDescriptor.fieldId === 'taxId';

          return (
            <PersonalDataInputField
              key={fieldDescriptor.fieldId}
              ariaLabel={fieldDescriptor.label}
              dataUi="registration-identity-personal-data-field"
              inputMode={fieldDescriptor.inputMode}
              invalidMessage={
                isTaxIdField ? taxIdErrorMessage ?? undefined : undefined
              }
              isInvalid={isTaxIdField && isTaxIdInvalid}
              label={fieldDescriptor.label}
              mask={fieldDescriptor.mask}
              onValueChange={handleFieldChange(fieldDescriptor.fieldId)}
              onOpenHelp={isTaxIdField ? onOpenTaxIdHelp : undefined}
              placeholder={fieldDescriptor.placeholder}
              showHelpTip={fieldDescriptor.showHelpTip ?? false}
              tipAriaLabel={fieldDescriptor.tipAriaLabel}
              value={values[fieldDescriptor.fieldId]}
            />
          );
        })}
      </IdentityFieldsTrack>
    </PersonalDataFormContainer>
  );
}
